import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePortfolioData } from '../contexts/DataContext';
import { Code, Database, Server, GitBranch, GraduationCap, Languages, Award } from 'lucide-react';

const getL = (obj, lang) => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] || obj['pt-BR'] || '';
};

const About = () => {
  const { t, currentLanguage } = useLanguage();
  const { education, certifications, spokenLanguages, loading } = usePortfolioData();

  const highlights = [
    { icon: <Code className="text-lime-400" size={32} />, title: "15+", subtitle: { 'pt-BR': 'Anos de Experiência', 'en': 'Years of Experience', 'it': 'Anni di Esperienza' } },
    { icon: <Database className="text-lime-400" size={32} />, title: "Java Expert", subtitle: { 'pt-BR': 'Especialista', 'en': 'Specialist', 'it': 'Specialista' } },
    { icon: <Server className="text-lime-400" size={32} />, title: { 'pt-BR': 'Telecom • Finanças • Seguros', 'en': 'Telecom • Finance • Insurance', 'it': 'Telecom • Finanza • Assicurazioni' }, subtitle: { 'pt-BR': 'Setores', 'en': 'Sectors', 'it': 'Settori' } },
    { icon: <GitBranch className="text-lime-400" size={32} />, title: "SAFe / Scrum", subtitle: { 'pt-BR': 'Metodologias Ágeis', 'en': 'Agile Methods', 'it': 'Metodologie Agili' } }
  ];

  const labels = {
    education: { 'pt-BR': 'Formação', 'en': 'Education', 'it': 'Formazione' },
    certifications: { 'pt-BR': 'Certificações', 'en': 'Certifications', 'it': 'Certificazioni' },
    languages: { 'pt-BR': 'Idiomas', 'en': 'Languages', 'it': 'Lingue' }
  };

  if (loading) return null;

  return (
    <section id="about" data-testid="about-section" className="py-20 bg-gray-950">
      <div className="container max-w-7xl mx-auto px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-lime-400 uppercase tracking-tight mb-6">{t('about.title')}</h2>
          <div className="w-24 h-1 bg-lime-400 mx-auto"></div>
        </div>
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div className="space-y-8">
            <p className="text-base md:text-lg text-gray-300 leading-relaxed">{t('about.description')}</p>
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
              <h3 className="text-base md:text-lg font-bold text-lime-400 mb-4 uppercase tracking-wide">{t('about.focus')}</h3>
              <p className="text-sm md:text-base text-gray-300 leading-relaxed">{t('about.focusText')}</p>
            </div>
            {education.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <GraduationCap className="text-lime-400" size={24} />
                  <h3 className="text-base md:text-lg font-bold text-lime-400 uppercase tracking-wide">{labels.education[currentLanguage]}</h3>
                </div>
                <ul className="space-y-4">
                  {education.map((edu) => (
                    <li key={edu.id} className="flex items-start">
                      <div className="w-2 h-2 bg-lime-400 rounded-full mr-4 mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-white font-medium text-sm">{getL(edu.degree, currentLanguage)}</p>
                        <p className="text-gray-400 text-xs">{edu.institution} - {edu.year}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {certifications.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <Award className="text-lime-400" size={24} />
                  <h3 className="text-base md:text-lg font-bold text-lime-400 uppercase tracking-wide">{labels.certifications[currentLanguage]}</h3>
                </div>
                <ul className="space-y-4">
                  {certifications.map((cert) => (
                    <li key={cert.id} className="flex items-start">
                      <div className="w-2 h-2 bg-lime-400 rounded-full mr-4 mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-white font-medium text-sm">{getL(cert.name, currentLanguage)}</p>
                        <p className="text-gray-400 text-xs">{cert.issuer} - {cert.year}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {spokenLanguages.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <Languages className="text-lime-400" size={24} />
                  <h3 className="text-base md:text-lg font-bold text-lime-400 uppercase tracking-wide">{labels.languages[currentLanguage]}</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {spokenLanguages.map((lang) => (
                    <div key={lang.id} className="flex items-center space-x-3">
                      <span className="text-2xl">{lang.flag}</span>
                      <div>
                        <p className="text-white font-medium text-sm">{getL(lang.language, currentLanguage)}</p>
                        <p className="text-gray-400 text-xs">{getL(lang.level, currentLanguage)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-6">
            {highlights.map((h, i) => (
              <div key={i} data-testid={`highlight-card-${i}`} className="bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 hover:border-lime-400/30">
                <div className="mb-4 flex justify-center">{h.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{typeof h.title === 'object' ? getL(h.title, currentLanguage) : h.title}</h3>
                <p className="text-gray-400 font-medium uppercase tracking-wide text-xs">{getL(h.subtitle, currentLanguage)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
